import { Text } from '@/components/animated-type/text';
import { AnnotatedType } from '@/components/annotated-type';
import { StoryHeader, StoryTagline, StoryTheme, StoryTitle } from '@/components/microstory/header';
import { Outro } from '@/components/microstory/outro';
import { StoriesNavigation } from '@/components/microstory/stories-navigation';
import { Theme, Themes } from '@/components/microstory/theme';
import { SeoHead } from '@/components/seo-head';
import { EqualityForSomeChapter2 } from '@/components/stories/equality-for-some/chapter-2';
import { EqualityForSomeChapter3 } from '@/components/stories/equality-for-some/chapter-3';
import { SurveyBackground } from '@/components/survey-background';
import data from '@/data/equality-for-some.json';
import { createGetServerSideProps } from '@/utils/create-get-server-side-props';
import { getStoryProps } from '@/utils/get-story-props';
import { useLayout } from '@/utils/use-layout';
import { t, Trans } from '@lingui/macro';
import { InferGetServerSidePropsType } from 'next';
import { useSharingImage } from '@/utils/use-sharing-image';
import { useLingui } from '@lingui/react';

export const getServerSideProps = createGetServerSideProps(
  async () => ({ data: data as EqualityForSomeData }),
  getStoryProps('equality-for-some', 'eyes-on-equality')
);

export default function EqualityForSome(
  props: InferGetServerSidePropsType<typeof getServerSideProps>
) {
  const { storySlug, baseUrl, data, sharingUrl, linkedStoryIds, relatedStoryId } = props;

  useLayout('white');
  useLingui();

  const sharingImage = useSharingImage('equality-for-some');
  const pageTitle = t({ id: 'equality-for-some.title', message: 'Equality for some' });
  const pageDescription = t({
    id: 'equality-for-some.description',
    message:
      'Attitudes toward the rights of LGBTQ+ people have shifted rapidly in many countries over the past three decades. But discrimination persists.',
  });

  return (
    <>
      <SeoHead
        baseUrl={baseUrl}
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        sharingImage={sharingImage}
      />

      <Theme theme={Themes.hutspot} hasBackButton>
        <StoryHeader backgroundId={storySlug}>
          <StoryTheme>
            <Trans id="equality-for-some.theme">Discrimination</Trans>
          </StoryTheme>
          <StoryTitle>{pageTitle}</StoryTitle>
          <StoryTagline>
            <Trans id="equality-for-some.tagline">
              <Text type="heading">
                Attitudes toward the <span>rights of LGBTQ+ people</span> have shifted rapidly in
                many countries over the past three decades. But discrimination persists.
              </Text>
            </Trans>
          </StoryTagline>
        </StoryHeader>

        <SurveyBackground />

        {/* Chapter1 was removed here. */}
        <EqualityForSomeChapter2 data={data.lgbtq_equality_country_bd} />
        <EqualityForSomeChapter3 data={data.lgbtq_equality_young_gen} />

        <Outro
          relatedStoryId={relatedStoryId}
          sharing={{
            title: pageTitle,
            url: sharingUrl,
            text: t({
              id: 'equality-for-some.sharing.text',
              message:
                'Attitudes toward the rights of LGBTQ+ people have shifted rapidly in many countries over the past three decades. But discrimination persists.',
            }),
          }}
        >
          <Trans id="equality-for-some.closing-question">
            How can we encourage more young men to{' '}
            <AnnotatedType>strengthen their support</AnnotatedType> for equality?
          </Trans>
        </Outro>
      </Theme>

      <StoriesNavigation storyIds={linkedStoryIds} />
    </>
  );
}
